import '../../styles/pages.css'
import React, { useCallback, useMemo, useState } from 'react'
import { List as TypeList } from 'infrastructure/api'
import { PaginatedResourceList } from './resourceList/PaginatedResourceList'
import { Box, Button, TextField, Typography } from '@mui/material'
import { Add } from '@mui/icons-material'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import { useNavigateTo } from 'infrastructure/routing'
import { useResourceRoutes } from '../../api/resourceRouting'
import { useListFilters } from '../../api/list.hooks'
import { deleteManyUsers } from '../../providers/mutations'
import { StringFilter } from './resourceList/StringFilter'
import { SelectFilter } from './resourceList/SelectFilter'
import { SwitchFilter } from './resourceList/SwitchFilter'
import { Column } from 'shared/ui/Table/types'
import { SortableHeader } from '../utility/SortableHeader'
import { CSVUpload } from 'shared/ui'

const roles = [
  { id: 'admin', name: 'admin' },
  { id: 'administrator', name: 'administrator' },
  { id: 'bot', name: 'bot' },
  { id: 'collaborator', name: 'collaborator' },
  { id: 'coteacher', name: 'coteacher' },
  { id: 'district-admin', name: 'district-admin' },
  { id: 'publisher', name: 'publisher' },
  { id: 'student', name: 'student' },
  { id: 'super-admin ', name: 'super-admin ' },
  { id: 'teacher', name: 'teacher' },
  { id: 'editor', name: 'school editor' },
  { id: 'district-editor', name: 'district-editor' },
  { id: 'data-wiping-admin', name: 'data-wiping-admin' },
  { id: 'view-only-admin', name: 'view-only-admin' },
]

function UserFilters() {
  const navigate = useNavigateTo()
  const { create } = useResourceRoutes('users')
  const { filters } = useListFilters()

  const handleCreate = () => {
    const schoolId = filters.school
    // If the user list is filtered by a numeric school ID, pass it to the
    // create page so the school field is auto-populated.
    const url =
      schoolId && Number(schoolId) ? `${create}?school_id=${schoolId}` : create
    navigate(url)
  }

  return (
    <Box
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box>
        <StringFilter filterKey="email" label="Email" />
        <StringFilter filterKey="user" label="User name/ID" />
        <StringFilter filterKey="school" label="School name/ID" />
        <StringFilter filterKey="district" label="District name/ID" />
        <SelectFilter label="Role" filterKey="role" options={roles} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <SwitchFilter
            label="Include Soft Deleted"
            filterKey="includeSoftDeleted"
          />
          <SwitchFilter label="Only Deleted" filterKey="onlyDeleted" />
          <SwitchFilter
            label="Include Demo Users"
            filterKey="includeDemoUsers"
          />
        </div>
      </Box>
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <CSVUpload />
          <Button variant="text" startIcon={<Add />} onClick={handleCreate}>
            Create
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

function OffboardWorkflow() {
  const [email, setEmail] = useState('')
  const { setFilter } = useListFilters()

  const handleSearch = () => {
    const trimmed = email.trim()
    if (!trimmed) return
    setFilter('email', trimmed)
    setFilter('includeSoftDeleted', 'true')
  }

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        <PersonOffIcon
          fontSize="small"
          sx={{ verticalAlign: 'middle', mr: 0.5 }}
        />
        Offboard Employee
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Search by email to find all accounts across districts and schools, then
        use &quot;Delete All Filtered&quot; to deactivate them.
      </Typography>
      <Box display="flex" gap={1} alignItems="center">
        <TextField
          size="small"
          label="Employee Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch()
          }}
          placeholder="user@example.com"
          sx={{ width: 300 }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={!email.trim()}
        >
          Find Accounts
        </Button>
      </Box>
    </Box>
  )
}

export function UserList() {
  const userColumns = useUserColumns()
  const onBulkDelete = useCallback((ids: (number | string)[]) => {
    return deleteManyUsers(ids as number[])
  }, [])
  return (
    <div>
      <OffboardWorkflow />
      <div>
        - You can use wildcard character ({'"*"'}) for email and user name
        searches
      </div>
      <PaginatedResourceList
        title="Users"
        resourceKey="users"
        columns={userColumns}
        header={<UserFilters />}
        onBulkDelete={onBulkDelete}
      />
    </div>
  )
}

function useUserColumns(): Column<TypeList.User>[] {
  return useMemo(
    () => [
      {
        title: <SortableHeader title={'Id'} id={'id'} />,
        width: 75,
        rowContent({ id }: TypeList.User) {
          return id
        },
      },
      {
        title: <SortableHeader title={'Name'} id={'name'} />,
        width: 100,
        rowContent({ name }: TypeList.User) {
          return name
        },
      },
      {
        title: <SortableHeader title={'Email'} id={'email'} />,
        width: 100,
        rowContent({ email }: TypeList.User) {
          return email
        },
      },
      {
        title: 'Roles',
        width: 100,
        rowContent({ roles }: TypeList.User) {
          return roles.join(', ')
        },
      },
      {
        title: <SortableHeader title={'School'} id={'school.name'} />,
        width: 100,
        rowContent({ school }: TypeList.User) {
          return school?.name ?? ''
        },
      },
      {
        title: (
          <SortableHeader title={'District'} id={'school.district.name'} />
        ),
        width: 100,
        rowContent({ school }: TypeList.User) {
          return school?.district?.name ?? ''
        },
      },
      {
        title: 'Classes',
        width: 100,
        rowContent({ classes }: TypeList.User) {
          return classes.length
        },
      },
      {
        title: 'Kiddom Access',
        width: 100,
        rowContent(user: TypeList.User) {
          let status = (
            <span style={{ color: 'green', textAlign: 'center' }}>Enabled</span>
          )
          if (user.deleted_at !== null) {
            status = (
              <span style={{ color: 'red', textAlign: 'center' }}>Deleted</span>
            )
          }
          return status
        },
      },
    ],
    [],
  )
}
